import { mapValuesDeep, scrollToError } from '@application/utils';
import { css } from '@emotion/react';
import type {
  BuyerAnimalFormMethods,
  BuyerAnimalFormValues,
  SelectOption,
} from '@presentation/components';
import {
  Accordion,
  Button,
  BuyerAnimalForm,
  ErrorText,
  InputField,
  InputLabel,
  InputSelectField,
  InputWrapper,
} from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import type { FormikProps, FormikSharedConfig } from 'formik';
import { useFormikContext } from 'formik';
import type { FC, MouseEventHandler } from 'react';
import { useCallback, useMemo, useRef } from 'react';
import { FaPlus, FaTimes } from 'react-icons/fa';
import * as Styled from '../styled';
import type { BookingPageFormValues, BookingPageProps } from '../types';

// TODO: Clean this mess of a comopnent

export const BookingPageAnimal: FC<BookingPageProps> = (props) => {
  const {
    formOptions,
    onNextClick,
    animalFormComponentProps,
    accountAnimals,
    shouldRequirePhoneNumber,
    forMultiAnimals,
  } = props;
  const { values, errors, touched, setFieldValue, validateForm, setTouched } =
    useFormikContext<BookingPageFormValues>();

  const { translate } = useTranslation();

  const selectAnimalOptions = useMemo(() => {
    if (forMultiAnimals) return formOptions.animalOptions;
    return [
      ...formOptions.animalOptions,
      {
        value: 'new',
        label: translate('common.buttons.newAnimal'),
      } as SelectOption<'new'>,
    ];
  }, [formOptions.animalOptions, forMultiAnimals, translate]);

  const customAnimalSubmitButton = useCallback(
    (formProps: FormikProps<BuyerAnimalFormValues> & FormikSharedConfig<BuyerAnimalFormValues>) => {
      return (
        <Button
          color="blue"
          size="large"
          onClick={async (event) => {
            setTouched({ bookingReason: true, animalIds: true, phoneNumber: true });
            formProps.setTouched(mapValuesDeep(formProps.initialValues, () => true));

            const isValid =
              !Object.keys(await validateForm()).length &&
              !Object.keys(await formProps.validateForm()).length;
            scrollToError({ ...errors, ...formProps.errors });
            if (!isValid) return;
            setFieldValue('animals', [formProps.values]);
            onNextClick?.(event);
          }}
        >
          {translate('common.buttons.next')}
        </Button>
      );
    },
    [translate, setTouched, validateForm, setFieldValue, onNextClick, errors],
  );

  const forms = useRef<HTMLFormElement[]>([]);

  const scrollToLastAccordionTab = useCallback(() => {
    window.requestAnimationFrame(() => {
      const element = document.querySelector('[data-last-booking-according="true"]');
      if (!element) return;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 200;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    });
  }, []);

  const closeClickHandler = useCallback(
    (index: number) => {
      const notCurrentIndex = (_: unknown, i: number) => i !== index;
      forms.current = (forms.current ?? []).filter(notCurrentIndex);
      setFieldValue('animalIds', (values?.animalIds).filter(notCurrentIndex));
    },
    [forms, setFieldValue, values?.animalIds],
  );

  const formRefHandler = useCallback(
    (form: HTMLFormElement | null, index: number) => {
      if (!form) return;
      const newForms = [...(forms.current ?? [])];
      newForms[index] = form;
      forms.current = newForms;
    },
    [forms],
  );

  const submitClickHandler: MouseEventHandler = useCallback(
    async (event) => {
      setTouched({ bookingReason: true, animalIds: true, phoneNumber: true });
      const animals: BuyerAnimalFormValues[] = [];
      let isValid = !Object.keys(await validateForm()).length;
      for (let i = 0; i < (forms.current ?? []).length; i += 1) {
        const form = forms.current[i] as unknown as BuyerAnimalFormMethods;
        // eslint-disable-next-line no-continue
        if (!form.validateForm) continue;
        // eslint-disable-next-line no-await-in-loop
        const formErrors = await form.validateForm();
        const isFormValid = !Object.keys(formErrors).length;
        if (!isFormValid) {
          isValid = false;
        } else {
          animals.push(form.getValues());
        }
      }
      if (!isValid) return;
      setFieldValue('animals', animals);
      onNextClick?.(event);
    },
    [onNextClick, setFieldValue, setTouched, validateForm],
  );

  return (
    <>
      <Styled.BookingSubtitle>{translate('booking.steps.info')}</Styled.BookingSubtitle>

      {shouldRequirePhoneNumber && (
        <InputWrapper>
          <InputLabel required>{translate('common.inputs.mobilePhone.label')}</InputLabel>
          <InputField
            type="tel"
            name="phoneNumber"
            onBlur={(event) => {
              setFieldValue('phoneNumber', event.target.value.replaceAll(' ', ''));
            }}
          />
        </InputWrapper>
      )}
      <InputWrapper>
        <InputLabel required>{translate('common.inputs.visitReason.label')}</InputLabel>
        <InputField
          style={{ minHeight: 200 }}
          textarea
          required
          name="bookingReason"
          placeholder={translate('common.inputs.visitReason.placeholder')}
        />
      </InputWrapper>
      {!!formOptions.animalOptions?.length && (
        <InputWrapper>
          <InputLabel required>
            {translate(
              `common.inputs.${forMultiAnimals ? 'selectMultipleAnimal' : 'selectAnimal'}.label`,
            )}
          </InputLabel>
          <InputSelectField<BookingPageFormValues['animalIds'][0]>
            hasDropdownIcon
            multiple={forMultiAnimals}
            alwaysTreatAsArray
            required
            name="animalIds"
            options={selectAnimalOptions}
            placeholder={translate('common.inputs.selectAnimal.placeholder')}
            onSelect={() => {
              scrollToLastAccordionTab();
            }}
          />
        </InputWrapper>
      )}
      {!forMultiAnimals && (
        <>
          <InputWrapper
            css={css`
              ${values.animalIds.length ? 'display: block;' : 'display: none;'}
            `}
          >
            <BuyerAnimalForm
              isBookingForm
              {...animalFormComponentProps(values.animalIds?.[0])}
              form={{
                ...animalFormComponentProps(values.animalIds?.[0]).form,
                initialValues: values.animals?.[0]
                  ? values.animals?.[0]
                  : animalFormComponentProps(values.animalIds?.[0]).form.initialValues,
              }}
              customSubmitButtons={customAnimalSubmitButton}
            />
          </InputWrapper>
          {!values.animalIds.length && (
            <Button
              color="blue"
              size="large"
              onClick={async (event) => {
                setTouched({ bookingReason: true, animalIds: true, phoneNumber: true });
                const isValid = !Object.keys(await validateForm()).length;
                if (!isValid) return;
                onNextClick?.(event);
              }}
            >
              {translate('common.buttons.next')}
            </Button>
          )}
        </>
      )}
      {forMultiAnimals && (
        <>
          <div>
            {formOptions.animalOptions.length === 0 && (
              <InputLabel required>{translate('common.inputs.animals.label')}</InputLabel>
            )}
          </div>
          {!!values.animalIds?.length && (
            <Accordion
              defaultChecked
              noScroll
              tabs={((values.animalIds ?? []) as (number | 'new')[]).map((animalId, index) => {
                const animal = (accountAnimals ?? []).find((x) => x.id === animalId);
                const formComponentProps = animalFormComponentProps(animalId);
                return {
                  title: (
                    <Styled.AnimalAccordionTabWrapper>
                      <FaTimes onClick={() => closeClickHandler(index)} />
                      <span>{animal?.name ?? translate('common.buttons.newAnimal')}</span>
                    </Styled.AnimalAccordionTabWrapper>
                  ),
                  content: (
                    <Styled.AnimalAccordionTabContent
                      data-last-booking-according={index === values.animalIds.length - 1}
                    >
                      <BuyerAnimalForm
                        isBookingForm
                        refId={(ref) => formRefHandler(ref, index)}
                        {...formComponentProps}
                        form={{
                          ...formComponentProps.form,
                          initialValues: values.animals?.[index]
                            ? values.animals?.[index]
                            : formComponentProps.form.initialValues,
                        }}
                        customSubmitButtons={() => <></>}
                      />
                    </Styled.AnimalAccordionTabContent>
                  ),
                };
              })}
            />
          )}

          <InputWrapper>
            <Styled.AnimalAddButton
              iconLeft={<FaPlus />}
              onClick={() => {
                setFieldValue('animalIds', [...values.animalIds, 'new']);
                scrollToLastAccordionTab();
              }}
              shrink
            >
              {translate('common.buttons.addNewAnimal')}
            </Styled.AnimalAddButton>
            {errors.animalIds && touched.animalIds && <ErrorText>{errors.animalIds}</ErrorText>}
          </InputWrapper>

          <Button color="blue" size="large" onClick={submitClickHandler}>
            {translate('common.buttons.next')}
          </Button>
        </>
      )}
    </>
  );
};
